import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import MuiButton from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled, useTheme } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { isSocialAuthConfigured, signInWithGoogle, signInWithMicrosoft } from "../../../utils/socialAuth";
import { setIsAuthLoading } from "../../../redux/reducers/session";

const SocialButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  color: theme.palette.brand.gray900,
  fontSize: 16,
  fontWeight: 600,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.brand.gray100,
  },
  "&:disabled": {
    backgroundColor: theme.palette.brand.white,
    opacity: 0.7,
  },
}));

const GoogleIcon: FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

const MicrosoftIcon: FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
    <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
    <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
    <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
  </svg>
);

type SocialAuthButtonsProps = {
  onError: (message: string) => void;
};

const SocialAuthButtons: FC<SocialAuthButtonsProps> = ({ onError }) => {
  const dispatch = useDispatch();
  const theme = useTheme<Theme>();
  const isAuthLoading = useSelector((state: ReduxApplicationState) => state.session.isLoading);

  if (!isSocialAuthConfigured()) {
    return null;
  }

  const handleSocialSignIn = async (provider: "google" | "microsoft") => {
    try {
      dispatch(setIsAuthLoading(true));
      if (provider === "google") {
        await signInWithGoogle();
      } else {
        await signInWithMicrosoft();
      }
    } catch (error) {
      const err = error as any;
      dispatch(setIsAuthLoading(false));
      onError(`There was an error logging in: ${err.message || "social sign-in failed"}`);
    }
  };

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" mb={2}>
        <Box flex={1}>
          <Divider />
        </Box>
        <Typography style={{ fontSize: 13, color: theme.palette.brand.gray500, padding: "0 12px" }}>or</Typography>
        <Box flex={1}>
          <Divider />
        </Box>
      </Box>

      <SocialButton
        type="button"
        variant="outlined"
        fullWidth
        disableElevation
        disabled={isAuthLoading}
        startIcon={<GoogleIcon />}
        onClick={() => handleSocialSignIn("google")}
      >
        Continue with Google
      </SocialButton>

      <Box mt={1.5}>
        <SocialButton
          type="button"
          variant="outlined"
          fullWidth
          disableElevation
          disabled={isAuthLoading}
          startIcon={<MicrosoftIcon />}
          onClick={() => handleSocialSignIn("microsoft")}
        >
          Continue with Microsoft
        </SocialButton>
      </Box>
    </Box>
  );
};

export default SocialAuthButtons;
